import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  PenLine,
  Baseline,
  Calendar,
  User,
  Mail,
  Building2,
  Type,
  CheckSquare,
  Check,
  FileText,
  X,
  Download,
  ArrowLeft,
  Loader2,
  Clock,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  getDocument,
  signDocument,
  declineDocument,
  viewDocument,
  fileUrl,
} from "../../api";
import DownloadFilesModal from "./DownloadFilesModal";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const RENDER_WIDTH = 760;
const CURSIVE = "'Brush Script MT', 'Segoe Script', cursive";

const FIELD_META = {
  signature: { label: "Signature", icon: PenLine },
  initials: { label: "Initials", icon: Baseline },
  date: { label: "Date Signed", icon: Calendar },
  name: { label: "Name", icon: User },
  email: { label: "Email", icon: Mail },
  company: { label: "Company", icon: Building2 },
  text: { label: "Text", icon: Type },
  checkbox: { label: "Checkbox", icon: CheckSquare },
};

const FIELD_CAPTION = {
  signature: "SIGNATURE",
  initials: "INITIAL",
  date: "DATE",
  name: "NAME",
  email: "EMAIL",
  company: "COMPANY",
  text: "TEXT",
  checkbox: "CHECKBOX",
};

const todayStr = () =>
  new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const isFieldFilled = (f, v) => {
  if (f.type === "date") return true; // auto-filled on signing
  if (f.type === "checkbox") return v === true;
  return v !== undefined && v !== null && v !== "";
};

// Derive uppercase initials from the account's real name, e.g. "Omar Jallow" -> "OJ".
const deriveInitials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0].toUpperCase())
    .join("");

// The auto value bound to the signer's own account (no manual typing allowed).
const autoSignValue = (field, user) =>
  field?.type === "initials" ? deriveInitials(user?.name) : (user?.name || "");

// ---------- Adopt & Sign confirm (no text input; uses the signer's own account name) ----------
const AdoptSignPad = ({ field, user, onClose, onSave }) => {
  const value = autoSignValue(field, user);
  const isInitials = field?.type === "initials";
  return (
    <Dialog open={!!field} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="z-[60] sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="text-left">
            {isInitials ? "Adopt your initials" : "Adopt your signature"}
          </DialogTitle>
          <DialogDescription className="text-left">
            Generated from your account name — no typing required.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#8B8680] dark:text-white/50">
            {isInitials
              ? "Your initials are generated from your account name."
              : "Your signature is generated from your account name."}
          </p>
          <div className="flex h-[130px] items-center justify-center rounded-xl border-2 border-dashed border-[#E7E0D8] bg-white dark:border-white/15">
            <span className="text-5xl text-[#1A1815]" style={{ fontFamily: CURSIVE }}>
              {value || "—"}
            </span>
          </div>
          <p className="text-xs text-[#B0A99F] dark:text-white/35">
            Signing as <span className="font-semibold text-[#1A1815] dark:text-white/70">{user?.name}</span>
          </p>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-xl border border-[#E7E0D8] px-5 py-2.5 text-sm font-medium text-[#1A1815] dark:border-white/10 dark:text-white"
          >
            Cancel
          </button>
          <button
            data-testid="signature-adopt-btn"
            onClick={() => value && onSave(value)}
            disabled={!value}
            className="rounded-xl bg-[#1A1815] px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-50 dark:bg-white dark:text-black"
          >
            Adopt &amp; Sign
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SignDocument = ({ user, company, onAction, dark }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [doc, setDoc] = useState(location.state?.doc || null);
  const [loading, setLoading] = useState(!location.state?.doc);
  const [pagesByDoc, setPagesByDoc] = useState({});
  const [values, setValues] = useState({});
  const [padField, setPadField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const activeCompany = location.state?.company || company;
  const accent = activeCompany?.accent || "#7A6FF0";

  // Always fetch the authoritative merged doc (full field set + every signer's
  // values) so prior signatures are visible even when we arrived with the stale
  // list copy in navigation state (which only holds the viewer's own fields).
  useEffect(() => {
    let alive = true;
    getDocument(id)
      .then((d) => {
        if (alive) {
          setDoc(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!alive) return;
        if (location.state?.doc) {
          setLoading(false);
        } else {
          onAction("Could not open document");
          navigate("/", { replace: true });
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fields = doc?.fields || [];
  const baseWidth = doc?.base_width || 820;
  const scale = RENDER_WIDTH / baseWidth;
  const docsList =
    doc?.documents && doc.documents.length
      ? doc.documents
      : [{ name: doc?.name, type: doc?.type, pages: doc?.pages || 1, storage_path: doc?.storage_path }];
  const pagesOf = (di, d) =>
    (d.type || "").toUpperCase() === "PDF" && d.storage_path ? pagesByDoc[di] || 0 : d.pages || 1;

  const canSign =
    !!doc &&
    doc.direction === "received" &&
    doc.status === "action_required" &&
    doc.owner_email === user?.email;

  const isWaiting = doc?.direction === "received" && doc?.status === "waiting";

  useEffect(() => {
    if (!doc) return;
    setValues({ ...(doc.field_values || {}) });
    if (doc.direction === "received" && !doc.viewed && doc.owner_email === user?.email) {
      viewDocument(doc.id).catch(() => {});
    }
    // Re-run when the merged field_values arrive (object identity changes on fetch)
    // so prior signers' values populate once the authoritative doc loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.field_values]);

  const fieldIsMine = (f) => !f.recipient_email || f.recipient_email === user?.email;
  const myFields = fields.filter(fieldIsMine);
  const filledCount = myFields.filter((f) => isFieldFilled(f, values[f.id])).length;
  const allDone = myFields.length === 0 || filledCount === myFields.length;

  const setValue = (fid, val) => setValues((prev) => ({ ...prev, [fid]: val }));

  const goBack = () => navigate("/");

  const handleSubmit = async () => {
    if (submitting || !allDone) return;
    setSubmitting(true);
    const finalValues = {};
    myFields.forEach((f) => {
      finalValues[f.id] = f.type === "date" ? todayStr() : values[f.id];
    });
    try {
      await signDocument(doc.id, finalValues);
      onAction("Document signed — status updated to Completed");
      navigate("/");
    } catch (e) {
      onAction(e?.response?.data?.detail || "Failed to submit signature");
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await declineDocument(doc.id);
      onAction("Document declined");
      navigate("/");
    } catch (e) {
      onAction(e?.response?.data?.detail || "Failed to decline");
      setSubmitting(false);
    }
  };

  const refId = ((doc?.envelope_id || doc?.id || "").replace(/-/g, "").slice(0, 10) || "QOSIGN").toUpperCase();
  const stampStr = () => {
    const d = doc?.signed_at ? new Date(doc.signed_at) : new Date();
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const CompletedMark = ({ f, children, isSignature, onClick }) => {
    const w = f.w * scale;
    const arm = Math.min(6, Math.max(3, w * 0.3));
    const R = Math.min(6, arm + 2);
    const T = 1.5;
    const cornerBase = { position: "absolute", left: 0, width: arm, height: R };
    return (
      <div
        key={f.id}
        data-testid={`view-field-${f.type}`}
        onClick={onClick}
        className="absolute"
        style={{ left: f.x * scale, top: f.y * scale, width: w, cursor: onClick ? "pointer" : "default" }}
      >
        <span
          className="block"
          style={{
            paddingLeft: arm + 2, fontSize: 8, lineHeight: "9px", fontWeight: 800,
            letterSpacing: "0.09em", color: accent, textTransform: "uppercase",
          }}
        >
          {FIELD_CAPTION[f.type] || "FIELD"}
        </span>
        <div
          style={{
            position: "relative",
            minHeight: f.h * scale,
            paddingLeft: arm + 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <span style={{ ...cornerBase, top: 0, borderTop: `${T}px solid ${accent}`, borderLeft: `${T}px solid ${accent}`, borderTopLeftRadius: R }} />
          <span style={{ position: "absolute", left: 0, top: R, bottom: R, width: T, background: accent }} />
          <span style={{ ...cornerBase, bottom: 0, borderBottom: `${T}px solid ${accent}`, borderLeft: `${T}px solid ${accent}`, borderBottomLeftRadius: R }} />
          {children}
          {isSignature && (
            <div style={{ marginTop: 2 }}>
              <div style={{ fontSize: 7, lineHeight: "8px", fontWeight: 700, color: accent }}>
                Signed via QoSign
              </div>
              <div style={{ height: 1, background: accent, opacity: 0.5, margin: "1.5px 0" }} />
              <div style={{ fontSize: 6.5, lineHeight: "8px", color: "#8B8680" }}>
                {stampStr()} · {refId}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderField = (f) => {
    const meta = FIELD_META[f.type] || FIELD_META.text;
    const Icon = meta.icon;
    const val = values[f.id];
    const hasVal = val !== undefined && val !== null && val !== "";
    const editable = canSign && fieldIsMine(f);
    const style = {
      left: f.x * scale,
      top: f.y * scale,
      width: f.w * scale,
      height: f.h * scale,
    };
    const pendColor = "#7A6FF0";
    const cursiveSize = Math.max(14, f.h * scale * 0.62);

    const completedContent = () => {
      if (f.type === "signature" || f.type === "initials") {
        return (
          <span style={{ fontFamily: CURSIVE, fontSize: cursiveSize, lineHeight: 1, color: "#1A1815" }}>
            {val}
          </span>
        );
      }
      if (f.type === "checkbox") {
        return <Check className="h-4 w-4 text-[#087443]" strokeWidth={3} />;
      }
      return (
        <span className="truncate" style={{ fontSize: 11, fontWeight: 600, color: "#1A1815" }}>
          {val}
        </span>
      );
    };

    const isDone = isFieldFilled(f, val) && (f.type !== "date" || hasVal);

    // Signature / initials: click to adopt (auto account name); once adopted show the mark.
    if (f.type === "signature" || f.type === "initials") {
      if (hasVal) {
        return (
          <CompletedMark
            key={f.id}
            f={f}
            isSignature={f.type === "signature"}
            onClick={editable ? () => setPadField(f) : undefined}
          >
            {completedContent()}
          </CompletedMark>
        );
      }
      if (editable) {
        return (
          <button
            key={f.id}
            data-testid={`view-field-${f.type}`}
            onClick={() => setPadField(f)}
            className="group absolute flex items-center gap-1.5 rounded-md border-2 border-dashed px-2 text-xs font-semibold shadow-sm"
            style={{ ...style, borderColor: pendColor, backgroundColor: "rgba(122,111,240,0.12)", color: "#5B4FD6" }}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            <span className="truncate">{meta.label}</span>
          </button>
        );
      }
      return (
        <div key={f.id} data-testid={`view-field-${f.type}`} className="absolute flex items-center gap-1.5 rounded-md border-2 px-2 text-xs font-semibold" style={{ ...style, borderColor: "#C9C2BA", backgroundColor: "rgba(201,194,186,0.14)", color: "#8B8680" }}>
          <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span className="truncate">{meta.label}</span>
        </div>
      );
    }

    // Editable fields for the signer (manual entry required)
    if (editable) {
      if (f.type === "text" || f.type === "name" || f.type === "email" || f.type === "company") {
        const suggestion =
          f.type === "name" ? user?.name : f.type === "email" ? user?.email : f.type === "company" ? activeCompany?.name : "";
        const placeholder = suggestion || meta.label;
        return (
          <div key={f.id} className="absolute" style={{ left: f.x * scale, top: f.y * scale, width: f.w * scale }}>
            {hasVal && (
              <span className="block" style={{ fontSize: 8, lineHeight: "9px", fontWeight: 800, letterSpacing: "0.09em", color: accent, textTransform: "uppercase" }}>
                {FIELD_CAPTION[f.type]}
              </span>
            )}
            <input
              data-testid={`view-field-${f.type}`}
              value={val || ""}
              onChange={(e) => setValue(f.id, e.target.value)}
              placeholder={placeholder}
              className="w-full bg-white/95 px-2 text-xs font-semibold text-[#1A1815] outline-none"
              style={
                hasVal
                  ? { height: f.h * scale, borderTop: `1.5px solid ${accent}`, borderLeft: `1.5px solid ${accent}`, borderBottom: `1.5px solid ${accent}`, borderRight: "none", borderRadius: "3px 0 0 3px" }
                  : { height: f.h * scale, border: `2px solid ${pendColor}`, borderRadius: 6 }
              }
            />
          </div>
        );
      }
      if (f.type === "checkbox") {
        const checked = !!val;
        if (checked) {
          return (
            <CompletedMark key={f.id} f={f} onClick={() => setValue(f.id, false)}>
              {completedContent()}
            </CompletedMark>
          );
        }
        return (
          <button
            key={f.id}
            data-testid={`view-field-${f.type}`}
            onClick={() => setValue(f.id, true)}
            className="absolute flex items-center justify-center rounded-md border-2 bg-white/95"
            style={{ ...style, borderColor: pendColor }}
          />
        );
      }
      // date → auto-filled at submit
      return (
        <div
          key={f.id}
          data-testid={`view-field-${f.type}`}
          className="absolute flex items-center gap-1.5 rounded-md border-2 border-dotted px-2 text-xs font-medium"
          style={{ ...style, borderColor: "#B0A99F", backgroundColor: "rgba(176,169,159,0.12)", color: "#8B8680" }}
          title="Filled automatically when you sign"
        >
          <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span className="truncate">Auto-dated</span>
        </div>
      );
    }

    // Read-only overlay (sender view or completed/declined doc)
    if (isDone) {
      return (
        <CompletedMark key={f.id} f={f} isSignature={f.type === "signature"}>
          {completedContent()}
        </CompletedMark>
      );
    }
    return (
      <div
        key={f.id}
        data-testid={`view-field-${f.type}`}
        className="absolute flex items-center gap-1.5 rounded-md border-2 px-2 text-xs font-semibold"
        style={{ ...style, borderColor: "#C9C2BA", backgroundColor: "rgba(201,194,186,0.14)", color: "#8B8680" }}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        <span className="truncate">{meta.label}</span>
      </div>
    );
  };

  const renderPage = (di, pageNum, contents) => (
    <div
      key={`${di}-${pageNum}`}
      className="relative mx-auto mb-6 overflow-hidden rounded-lg bg-white shadow-xl last:mb-0"
      style={{ width: RENDER_WIDTH }}
    >
      {contents}
      <div className="absolute inset-0 z-10">
        {!isWaiting &&
          fields
            .filter((f) => (f.docIndex || 0) === di && (f.page || 1) === pageNum)
            .map((f) => renderField(f))}
      </div>
    </div>
  );

  const statusBadge = () => {
    if (doc?.status === "waiting")
      return <span className="rounded-full bg-[#EFE9E2] px-2.5 py-1 text-[11px] font-bold text-[#8B8680] dark:bg-white/10 dark:text-white/55">Waiting</span>;
    if (doc?.status === "completed")
      return <span className="rounded-full bg-[#7AFFA1]/25 px-2.5 py-1 text-[11px] font-bold text-[#087443]">Completed</span>;
    if (doc?.status === "declined")
      return <span className="rounded-full bg-[#F2C0C0] px-2.5 py-1 text-[11px] font-bold text-[#B42318]">Declined</span>;
    if (doc?.status === "action_required")
      return <span className="rounded-full bg-[#F79009]/20 px-2.5 py-1 text-[11px] font-bold text-[#B54708] dark:text-[#FDB022]">Action Required</span>;
    return null;
  };

  if (loading || !doc) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#8B8680] dark:text-white/50" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="sign-document-page">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-30 border-b border-[#E7E0D8] bg-[#FBF7F2]/90 px-4 py-3 backdrop-blur-md dark:border-white/10 dark:bg-[#141019]/85 md:px-8">
        <div className="mx-auto flex max-w-[900px] items-center gap-3">
          <button
            data-testid="sign-back-btn"
            onClick={goBack}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2} />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-lg font-bold tracking-tight text-[#1A1815] dark:text-white" data-testid="viewer-title">
                {doc?.subject || doc?.name}
              </h1>
              {statusBadge()}
            </div>
            {doc?.sender_name && (
              <p className="truncate text-xs text-[#8B8680] dark:text-white/45">
                {doc.direction === "received" ? "From " : "Sent by "}
                {doc.sender_name}
                {doc.recipients ? ` · to ${doc.recipients}` : ""}
              </p>
            )}
          </div>
          {canSign && (
            <span
              data-testid="viewer-progress"
              className="hidden shrink-0 rounded-full bg-[#7A6FF0]/15 px-3 py-1.5 text-xs font-semibold text-[#5B4FD6] dark:text-[#B7AEFF] sm:inline"
            >
              {filledCount}/{myFields.length} fields completed
            </span>
          )}
        </div>
      </header>

      {/* Scrollable document body */}
      <main className="mx-auto max-w-[900px] px-4 pb-32 pt-6 md:px-8">
        {isWaiting && (
          <div
            data-testid="waiting-banner"
            className="mx-auto mb-6 flex max-w-[760px] items-center gap-3 rounded-xl border border-[#E7E0D8] bg-[#F6EFE8] px-4 py-3.5 text-sm text-[#1A1815] dark:border-white/10 dark:bg-white/5 dark:text-white/80"
          >
            <Clock className="h-5 w-5 shrink-0 text-[#8B8680] dark:text-white/50" strokeWidth={1.9} />
            <span>
              <span className="font-semibold">Waiting for your turn.</span> This envelope uses a
              signing order — you can sign once the recipients before you have completed.
            </span>
          </div>
        )}
        {doc?.message && !isWaiting && doc?.status !== "completed" && doc?.status !== "declined" && (
          <div className="mx-auto mb-6 max-w-[760px] rounded-xl bg-white/70 px-4 py-3 text-sm text-[#1A1815] dark:bg-white/5 dark:text-white/80">
            {doc.message}
          </div>
        )}
        {docsList.map((d, di) => {
          const dIsPdf = (d.type || "").toUpperCase() === "PDF" && !!d.storage_path;
          const dPages = pagesOf(di, d);
          return (
            <div key={di} className="mb-8 last:mb-0" data-testid={`sign-doc-${di}`}>
              {docsList.length > 1 && (
                <div
                  data-testid={`doc-heading-${di}`}
                  className="mx-auto mb-3 flex max-w-[760px] items-center gap-2 text-sm font-semibold text-[#1A1815] dark:text-white"
                >
                  <FileText className="h-4 w-4 text-[#8B8680]" strokeWidth={1.8} />
                  <span className="truncate">{d.name || `Document ${di + 1}`}</span>
                  <span className="ml-auto text-xs font-normal text-[#8B8680] dark:text-white/45">
                    Document {di + 1} of {docsList.length}
                  </span>
                </div>
              )}
              {dIsPdf ? (
                <Document
                  file={fileUrl(d.storage_path)}
                  onLoadSuccess={({ numPages: n }) => setPagesByDoc((prev) => ({ ...prev, [di]: n }))}
                  onLoadError={() => onAction("Could not render document")}
                  loading={<p className="py-16 text-center text-sm text-[#8B8680] dark:text-white/45">Loading document…</p>}
                >
                  {Array.from({ length: dPages }, (_, idx) =>
                    renderPage(
                      di,
                      idx + 1,
                      <Page
                        pageNumber={idx + 1}
                        width={RENDER_WIDTH}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                    )
                  )}
                </Document>
              ) : (
                Array.from({ length: dPages }, (_, idx) =>
                  renderPage(
                    di,
                    idx + 1,
                    <div
                      className="flex flex-col items-center justify-start bg-white p-10 dark:bg-white/95"
                      style={{ height: RENDER_WIDTH * 1.3 }}
                    >
                      <FileText className="mt-16 h-12 w-12 text-[#C9C2BA]" strokeWidth={1.4} />
                      <p className="mt-4 text-sm font-semibold text-[#8B8680]">{d.name}</p>
                      <p className="text-xs text-[#B0A99F]">
                        Page {idx + 1} of {dPages} · preview unavailable
                      </p>
                    </div>
                  )
                )
              )}
            </div>
          );
        })}
      </main>

      {/* Sticky bottom action bar */}
      {canSign && (
        <footer className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E7E0D8] bg-[#FBF7F2]/95 px-4 py-4 backdrop-blur-md dark:border-white/10 dark:bg-[#141019]/90 md:px-8">
          <div className="mx-auto flex max-w-[900px] items-center justify-between gap-3">
            {confirmDecline ? (
              <div className="flex w-full items-center justify-between gap-3">
                <span className="text-sm font-medium text-[#B42318]">Decline this document?</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmDecline(false)}
                    className="rounded-xl border border-[#E7E0D8] px-4 py-2.5 text-sm font-medium text-[#1A1815] dark:border-white/10 dark:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="confirm-decline-btn"
                    onClick={handleDecline}
                    disabled={submitting}
                    className="rounded-xl bg-[#B42318] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Yes, decline
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span className="hidden text-xs text-[#8B8680] dark:text-white/45 sm:block">
                  {filledCount}/{myFields.length} required fields completed
                </span>
                <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end">
                  <button
                    data-testid="decline-btn"
                    onClick={() => setConfirmDecline(true)}
                    disabled={submitting}
                    className="flex items-center gap-2 rounded-xl border border-[#E5484D] px-5 py-2.5 text-sm font-semibold text-[#B42318] transition-colors hover:bg-[#F2C0C0]/40 disabled:opacity-50 dark:text-[#F97066]"
                  >
                    <X className="h-4 w-4" strokeWidth={2} />
                    Decline
                  </button>
                  <button
                    data-testid="complete-sign-btn"
                    onClick={handleSubmit}
                    disabled={submitting || !allDone}
                    className="flex items-center gap-2 rounded-xl bg-[#12B76A] px-7 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {submitting ? "Submitting…" : "Sign & Complete"}
                    <Check className="h-4 w-4" strokeWidth={2.2} />
                  </button>
                </div>
              </>
            )}
          </div>
        </footer>
      )}

      {doc?.status === "completed" && (
        <footer className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E7E0D8] bg-[#FBF7F2]/95 px-4 py-4 backdrop-blur-md dark:border-white/10 dark:bg-[#141019]/90 md:px-8">
          <div className="mx-auto flex max-w-[900px] items-center justify-between gap-3">
            <span className="text-xs text-[#8B8680] dark:text-white/45">
              Completed &middot; QoSign verified
            </span>
            <button
              data-testid="download-signed-pdf-btn"
              onClick={() => setDownloadOpen(true)}
              className="flex items-center gap-2 rounded-xl bg-[#1A1815] px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03] active:scale-95 dark:bg-white dark:text-black"
            >
              <Download className="h-4 w-4" strokeWidth={1.9} />
              Download signed copy
            </button>
          </div>
        </footer>
      )}

      <AdoptSignPad
        field={padField}
        user={user}
        onClose={() => setPadField(null)}
        onSave={(text) => {
          setValue(padField.id, text);
          setPadField(null);
        }}
      />

      <DownloadFilesModal
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        docId={doc?.id}
        baseName={doc?.subject || doc?.name || "document"}
        onAction={onAction}
      />
    </div>
  );
};

export default SignDocument;
